import {
  Box,
  Button,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  SvgIcon,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CampaignContext } from "../../Context";
import GroupSelection from "./GroupSelection";
import TagSelection from "./TagSelection";
import {
  allowedEmailCounts,
  recipientCount,
} from "screens/Campaigns/CampaignService";
import {
  getFormattedStatsCount,
  handleErrorMessage,
  isTruthy,
  openErrorNotification,
  openInfoNotification,
} from "helpers/methods";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import SuggestionBox from "screens/Shared/SuggestionBox";

const options = [
  {
    label: "Groups",
    value: 1,
  },
  {
    label: "Tags",
    value: 2,
  },
];

const RecipientsSection = () => {
  const {
    managingStep,
    setManagingStep,
    submitCampaign,
    setSubmitCampaign,
    saveAsDraft,
  } = useContext(CampaignContext);
  const [selectedAudienceType, setSelectedAudienceType] = useState({
    label: "Groups",
    value: 1,
  });
  const [selectedAudienceIds, setSelectedAudienceIds] = useState<number[]>(
    submitCampaign.campaignSegments.segmentIds ?? []
  );
  const [selectedRecipientCount, setSelectedRecipientCount] =
    useState<number>(0);
  const [allowedEmailCount, setAllowedEmailCount] = useState<number | null>(
    null
  );
  const [recipientCountLoading, setRecipientCountLoading] =
    useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    retrieveAllowedEmails();
  }, []);

  useEffect(() => {
    if (selectedAudienceIds.length > 0 && isTruthy(allowedEmailCount)) {
      retrieveRecipientCount();
    } else {
      setSelectedRecipientCount(0);
    }
  }, [selectedAudienceIds, allowedEmailCount]);

  const stepCompletion = () => {
    return submitCampaign.campaignSegments.segmentIds.length > 0;
  };

  const retrieveAllowedEmails = async () => {
    try {
      setRecipientCountLoading(true);
      const response = await allowedEmailCounts();
      const count =
        (response.allowedEmailCount ?? 0) - (response.emailCount ?? 0);
      setAllowedEmailCount(count > 0 ? count : 0);
      setRecipientCountLoading(false);
    } catch (error: any) {
      setRecipientCountLoading(false);
      handleErrorMessage(error);
    }
  };

  const retrieveRecipientCount = async () => {
    try {
      setRecipientCountLoading(true);
      const response = await recipientCount(
        selectedAudienceType.value,
        selectedAudienceIds
      );
      if (response > allowedEmailCount!) {
        if (submitCampaign.campaign.type === "ABTesting") {
          openErrorNotification(
            `The requested email count of ${response} exceeds your permissible limit of ${allowedEmailCount!}.`
          );
          setSelectedAudienceIds((prev) => prev.slice(0, -1));
        } else {
          setSelectedRecipientCount(response);
        }
      } else {
        setSelectedRecipientCount(response);
      }
      setRecipientCountLoading(false);
    } catch (error: any) {
      setRecipientCountLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleSave = async () => {
    if (
      submitCampaign.campaign.type === "ABTesting" &&
      selectedRecipientCount < 20
    ) {
      return openInfoNotification(
        "Select atleast 20 recipients to perform A/B testing"
      );
    }
    setSaving(true);
    const campaign = {
      ...submitCampaign,
      campaignSegments: {
        ...submitCampaign.campaignSegments,
        segmentIds: selectedAudienceIds,
        recipients: selectedRecipientCount,
        segmentTypeId : selectedAudienceType.value,
      },
    };
    setSubmitCampaign(campaign);
    await saveAsDraft(campaign);
    setManagingStep("");
    setSaving(false);
  };

  const getExpandedViewBody = () => {
    return (
      <Stack spacing={4} width="100%">
        <Stack spacing={0.5}>
          <Typography variant="h4">Recipients</Typography>
          <Typography variant="body1">
            Select your target audience for better engagement
          </Typography>
        </Stack>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography>Send to</Typography>
            <Select
              size="small"
              value={selectedAudienceType.value}
              onChange={(event: any) => {
                const option = options.find(
                  (o) => o.value === event.target.value
                );
                if (option) {
                  setSelectedAudienceType(option);
                  setSelectedAudienceIds([]);
                  setSelectedRecipientCount(0);
                }
              }}
            >
              {options.map((option, index) => (
                <MenuItem value={option.value} key={index}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Stack>
          {selectedAudienceType.value === 1 ? (
            <GroupSelection
              selectedAudienceIds={selectedAudienceIds}
              setSelectedAudienceIds={setSelectedAudienceIds}
            />
          ) : (
            <TagSelection
              selectedAudienceIds={selectedAudienceIds}
              setSelectedAudienceIds={setSelectedAudienceIds}
            />
          )}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            {recipientCountLoading ? (
              <Skeleton
                animation="wave"
                variant="text"
                height={25}
                width={200}
              />
            ) : (
              <Typography variant="body1" fontWeight="600" fontSize="20px">
                {`${getFormattedStatsCount(selectedRecipientCount)} recipients`}
              </Typography>
            )}
            <Typography>{`${getFormattedStatsCount(
              allowedEmailCount!
            )} remaining emails`}</Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={(selectedRecipientCount / allowedEmailCount!) * 100}
            sx={{ height: "7px" }}
          />
          <Typography variant="body2" color="text.secondary">
            Send to as many recipients as you wish, within your plan limits.
          </Typography>
          {submitCampaign.campaign.type === "ABTesting" && (
            <SuggestionBox title="We recommend sending to more than 5,000 recipients, but the minimum requirement is 20 recipients for an A/B test campaign." />
          )}
          {submitCampaign.campaign.type !== "ABTesting" &&
            !recipientCountLoading &&
            selectedRecipientCount > allowedEmailCount! && (
              <Stack
                p={1}
                sx={{ backgroundColor: "#fff2f2", borderRadius: "8px" }}
              >
                <Typography
                  variant="body2"
                  color="#c23118"
                >{`The selected recipient count of ${getFormattedStatsCount(
                  selectedRecipientCount
                )} exceeds your remaining daily limit of ${allowedEmailCount!}. However, you can schedule this campaign for a future date if the limits for that date allows.`}</Typography>
              </Stack>
            )}
        </Stack>
      </Stack>
    );
  };

  const getExpandedView = () => {
    return (
      <Stack spacing={4}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" spacing={4} width="100%">
            <SvgIcon>
              <CheckCircleIcon
                htmlColor={stepCompletion() ? "#7551D0" : "#C1C1C1"}
              />
            </SvgIcon>
            {getExpandedViewBody()}
          </Stack>
          <Stack>
            <IconButton onClick={() => setManagingStep("")}>
              <SvgIcon>
                <CloseIcon />
              </SvgIcon>
            </IconButton>
          </Stack>
        </Stack>
        <Stack
          alignItems="center"
          justifyContent="flex-end"
          direction="row"
          spacing={1}
        >
          <Button
            size="small"
            variant="text"
            onClick={() => setManagingStep("")}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={selectedRecipientCount <= 0}
            onClick={handleSave}
          >
            Save
          </Button>
        </Stack>
      </Stack>
    );
  };

  const getNonExpandedView = () => {
    const onAnotherStep = managingStep !== "" && managingStep !== "recipients";
    return (
      <Grid
        container
        gap={1}
        justifyContent="space-between"
        sx={{ opacity: onAnotherStep ? 0.5 : 1 }}
      >
        <Stack direction="row" spacing={4}>
          <SvgIcon>
            <CheckCircleIcon
              htmlColor={stepCompletion() ? "#7551D0" : "#C1C1C1"}
            />
          </SvgIcon>
          <Stack spacing={0.5}>
            <Typography variant="h4">Recipients</Typography>
            <Typography variant="body1">
              {stepCompletion()
                ? `${selectedRecipientCount} recipients selected`
                : `Select your target audience for better engagement`}
            </Typography>
          </Stack>
        </Stack>
        <Stack>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setManagingStep("recipients");
            }}
            disabled={onAnotherStep}
          >
            {`${stepCompletion() ? "Edit" : "Add"}`} Recipients
          </Button>
        </Stack>
      </Grid>
    );
  };

  const getPage = () => {
    if (managingStep === "recipients") {
      return getExpandedView();
    }
    return getNonExpandedView();
  };

  return (
    <>
      <Box px={3}>{getPage()}</Box>
      <CustomLoader isLoading={saving} />
    </>
  );
};

export default RecipientsSection;
